import React, { Fragment, useMemo } from 'react'
import { makeStyles } from '@mui/styles'

import { menuItemsCenter } from '@/modules/layout/components/menu/config'
import MenuBarItem from './menu-bar-item'
import { MenuItems, SubscriptionTypeTypes } from '@/modules/base/util/constants'
import ThemeInterface from '@/theme-interface'
import WebStorageService from '@/modules/_services/webstorage.service'
import WebStorageKey from '@/enums/web-storage-key.enum'
import { ShoppingCartItems } from '@/modules/moduleSubscription/types/ShoppingCart'
import { useAppSelector } from '@/redux/store'
import { isMeinBuero } from '@/utils/build-env'

const isMB = isMeinBuero()

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 30,
    height: '100%',
  },
  divider: {
    color: theme.white,
    fontSize: theme.fontSizes.xlarge,
    fontWeight: theme.fontWeights.thin,
  },
}), { name: 'MenuBar' })

export const countShoppingCartItems = (items: ShoppingCartItems) => {
  let count = 0

  for (const key in items) {
    if (items.hasOwnProperty(key)) {
      const nestedItem = items[key]
      if (typeof nestedItem === 'object' && nestedItem !== null) {
        for (const nestedItemKey in nestedItem) {
          if (nestedItem.hasOwnProperty(nestedItemKey)) {
            count++
          }
        }
      }
    }
  }

  return count
}

const MenuBar = () => {
  const classes = useStyles()
  const userInfoRoles = useAppSelector(state => state.user.info.roles)
  const subscriptionsLoading = useAppSelector(state => state.subscription.details.loading)
  const appsMenuItems = useAppSelector(state => state.apps.menuItems)
  const subscriptions = useAppSelector(state => state.subscription.details.subscriptions)
  const moduleAccess = useAppSelector(state => state.permission.moduleAccess)
  const layoutSettings = useAppSelector(state => state.user.info.layoutSettings)
  const tenantSubscriptions = useAppSelector(state => state.tenant?.info?.subscriptions)
  const hasOnlyDocSub = !tenantSubscriptions?.filter(s => s.typeOfSubscription !== SubscriptionTypeTypes.Document).length

  const shoppingCart = WebStorageService.getItem(WebStorageKey.SHOPPING_CART)
  const shoppingCartItemsCount = countShoppingCartItems(shoppingCart?.items)

  const showUnavailableFeatures = layoutSettings?.showUnavailableFeatures ?? false
  const canSeeDisabledItems = !hasOnlyDocSub && !isMB && showUnavailableFeatures

  const menuItems = useMemo(() => {
    if (!userInfoRoles?.length) { return [] }

    const items = menuItemsCenter(appsMenuItems)
      .reduce((itemRepo, item) => {
        const { submenuItems, name, url } = item

        // don't add apps as it is displayed elsewhere
        if (name === MenuItems.Apps) {
          return itemRepo
        }

        // add staff and documents as they are only main menu points
        if (!submenuItems?.length && url) {
          itemRepo.push(item)
          return itemRepo
        }

        // only add items that have accessible submenu items
        // For admins/owners: show menu item even if all submenu items are disabled
        // For regular users and for meinbuero: only show if there are accessible submenu items
        const accessibleItem = submenuItems.find(subItem => subItem.read || subItem.write)
        if (accessibleItem || canSeeDisabledItems) {
          itemRepo.push(item)
        }

        return itemRepo
      }, [])

    return items.map((menuItemData) => {
      const { name } = menuItemData
      if (name === MenuItems.Marketplace) {
        return (
          <Fragment key={name + '-menuItemFragment'}>
            <div className={classes.divider}>|</div>
            <MenuBarItem badgeLabel={shoppingCartItemsCount || ''} {...menuItemData} />
          </Fragment>
        )
      }
      return (
        <MenuBarItem key={name} {...menuItemData} />
      )
    })
  }, [userInfoRoles, subscriptionsLoading, shoppingCartItemsCount, subscriptions, moduleAccess, canSeeDisabledItems, showUnavailableFeatures])

  if (subscriptionsLoading) { return null }

  return (
    <div key={'test-ddd'} className={classes.root}>
      {menuItems}
    </div>
  )
}

export default MenuBar
